import type { EacEvaluationWire, EacMode, EacReason } from '@forecastify/shared';
import { EAC_MODES } from '@forecastify/shared';
import type { Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 30 Oct 2026 · EV · the five EACs as both dashboard tabs draw them.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The server evaluates (`eac.ts`); this names. One menu of five, a disabled one greyed with its
 * reason and what would lift it; one line of notes under the EAC (the band, under-allocation, a
 * plan more optimistic than the trend). ⚠️ The data gaps are NOT drawn here — EV-8 made them the
 * project's alerts (`scan.ts`), so they sit on the Alerts tab and count against RISK.
 */

export function eacLabel(t: Translate, mode: EacMode): string {
  if (mode === 'budget') return t('eac.mode.budget');
  if (mode === 'trend') return t('eac.mode.trend');
  if (mode === 'schedule') return t('eac.mode.schedule');
  if (mode === 'resource') return t('eac.mode.resource');
  return t('eac.mode.adjusted');
}

export function eacHint(t: Translate, mode: EacMode): string {
  if (mode === 'budget') return t('eac.hint.budget');
  if (mode === 'trend') return t('eac.hint.trend');
  if (mode === 'schedule') return t('eac.hint.schedule');
  if (mode === 'resource') return t('eac.hint.resource');
  return t('eac.hint.adjusted');
}

export function eacFormula(mode: EacMode): string {
  if (mode === 'budget') return 'AC + (BAC − EV)';
  if (mode === 'trend') return 'AC + (BAC − EV) / CPI';
  if (mode === 'schedule') return 'AC + (BAC − EV) / (CPI × SPI(t))';
  if (mode === 'resource') return 'AC + Σ REM';
  return 'AC + Σ REM × r';
}

export function reasonText(t: Translate, reason: EacReason, eac: EacEvaluationWire): string {
  switch (reason) {
    case 'notStable':
      return t('eac.reason.notStable');
    case 'unestimated':
      return t('eac.reason.unestimated');
    case 'noCost':
      return t('eac.reason.noCost');
    case 'singleItemOverPlan':
      return t('eac.reason.singleItemOverPlan');
    case 'trendDisabled':
      return t('eac.reason.trendDisabled');
    case 'planThin':
      return t('eac.reason.planThin');
    case 'itemOverPlan':
      return t('eac.reason.itemOverPlan');
    case 'allocationEnded':
      return t('eac.reason.allocationEnded');
    case 'underAllocated':
      return t('eac.reason.underAllocated');
    case 'noSchedule':
      return t('eac.reason.noSchedule');
    case 'noCostRate':
      return t('eac.reason.noCostRate');
    case 'resourceDisabled':
      return t('eac.reason.resourceDisabled');
    case 'noHistory':
      return t('eac.reason.noHistory');
    case 'rOutOfRange':
      return t('eac.reason.rOutOfRange', { r: (eac.rRaw ?? 0).toFixed(2) });
  }
}

/** The mode on screen: the reader's pick, else the server's default, else Terv. */
export function effectiveMode(picked: EacMode | null, eac: EacEvaluationWire | null): EacMode {
  if (eac === null) return picked ?? 'budget';
  /* ⚠️ A pick made on another scope or day is kept only while it is enabled here — a disabled
     mode never draws a figure. */
  return picked !== null && eac.modes.some((one) => one.mode === picked && one.enabled)
    ? picked
    : eac.defaultMode;
}

/**
 * The five, in the summary's order. ⚠️ 2 Nov 2026 · every row's second line is what the mode IS,
 * enabled or not; a disabled one is greyed and says WHY in a tooltip on hover. It used to put the
 * reason where the description goes, so a reader choosing between five had to guess what the
 * greyed ones would have meant.
 */
export function eacMenuItems(
  t: Translate,
  eac: EacEvaluationWire | null,
  selected: EacMode,
  onSelect: (mode: EacMode) => void,
): {
  label: string;
  description: string;
  tooltip?: string;
  checked: boolean;
  disabled: boolean;
  onSelect: () => void;
}[] {
  return EAC_MODES.map((mode) => {
    const result = eac?.modes.find((one) => one.mode === mode);
    const enabled = result === undefined ? mode === 'budget' || mode === 'trend' : result.enabled;
    const why =
      enabled || result === undefined || eac === null
        ? ''
        : result.reasons.map((reason) => reasonText(t, reason, eac)).join(' ');
    return {
      label: eacLabel(t, mode),
      description: eacHint(t, mode),
      ...(why === '' ? {} : { tooltip: why }),
      checked: selected === mode,
      disabled: !enabled,
      onSelect: () => {
        onSelect(mode);
      },
    };
  });
}
